import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { usePathname } from 'next/navigation'
import { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { STORIES_BASE_PATH } from "@/constants/routes";
import { useApp } from "@/context/AppContext";

export default function useNavAnimation({
  isRouteChanging,
  isMapPage,
  isRecordPage,
  isStoryPage,
  isMobile,
  mediaQueryMotion,
  mediaQueryNoMotion,
}) {
  const [isHidden, setIsHidden] = useState(true);
  const nav = useRef(null);
  const isInitialMountRef = useRef(true);
  const delayRef = useRef(0);
  const isMobileRef = useRef(isMobile)
  const pathname = usePathname()
  const pathnameRef = useRef(pathname)

  const isHiddenRef = useRef(isHidden);
  const isMapPageRef = useRef(isMapPage);
  const isRecordPageRef = useRef(isRecordPage);
  const isStoryPageRef = useRef(isStoryPage);
  const isRouteChangingRef = useRef(isRouteChanging);
  const isInstant = useRef(false);

  const { isInitialLoad, aboutAnimationCompleted } = useApp()

  const animate = (animateOut, el) => {
    const mm = gsap.matchMedia();
    mm.add(mediaQueryMotion, () => {
      gsap.killTweensOf([el]);

      if (isInstant.current) {
        gsap.set([el], {
          y: animateOut ? '-200%' : '0%',
        });
        isInstant.current = false;
      } else {
        gsap.to([el], {
          y: animateOut ? '-200%' : '0%',
          duration: 1,
          ease: 'expo.out',
          delay: delayRef.current
        });
      }

    });

    mm.add(mediaQueryNoMotion, () => {
      gsap.killTweensOf([el]);

      if (isInstant.current) {
        gsap.set([el], {
          y: animateOut ? '-200%' : '0%',
        });
        isInstant.current = false;
      } else {
        gsap.to([el], {
          y: animateOut ? '-200%' : '0%',
          duration: 0,
          ease: 'expo.out',
          delay: delayRef.current
        });
      }

    });
  }

  const animateHeaders = (animateOut) => {
    animateNav(animateOut)
    animateAncestryHeader(animateOut)
    delayRef.current = 0;
  }

  const animateNav = (animateOut) => {
    animate(animateOut, nav.current)
  };

  const animateAncestryHeader = (animateOut) => {
    const ancestryHeader = document.documentElement.querySelector('#HeaderRegion')
    animate(animateOut, ancestryHeader)
  }

  // Handle isHidden changes with animation
  useEffect(() => {
    if (isHidden === isHiddenRef.current && isMapPage === isMapPageRef.current && isRecordPage === isRecordPageRef.current && isStoryPage === isStoryPageRef.current) {
      return
    }

    isHiddenRef.current = isHidden;

    if (isInitialMountRef.current && (isMapPage || isRecordPage || isStoryPage) && !isInitialLoad) {
      // animateAncestryHeader(isHidden);
      return;
    }

    isInitialMountRef.current = false;

    if ((isMapPage || isRecordPage || isStoryPage) && !isInitialLoad) {
      // animateAncestryHeader(isHidden);
      return
    } else {
      animateHeaders(isHidden);
    }
  }, [isHidden, isMapPage, isRecordPage, isStoryPage, isInitialLoad]);

  // Handle isMobile changes
  useEffect(() => {
    if (!hasAnimated.current) {
      return
    }

    if (isMobileRef.current === isMobile) {
      return
    }

    if (isMobile !== null && !isInitialMountRef.current) {
      if (!isMapPage && !isRecordPage && !isStoryPage) {
        animateHeaders(false);
      }
      // else {
      //   animateAncestryHeader(false);
      // }

      isMobileRef.current = isMobile
    }

  }, [isMobile, isMapPage, isRecordPage, isStoryPage]);

  useEffect(() => {
    pathnameRef.current = pathname;
    const headerEl = document.documentElement.querySelector('#HeaderRegion');
    const footerEl = document.documentElement.querySelector('#FooterRegion');

    if ((isMapPage || isRecordPage || isStoryPage) && !isInitialLoad) {
      headerEl.style.display = 'none';
      footerEl.style.display = 'none';

      headerEl.setAttribute('inert', true)
      footerEl.setAttribute('inert', true)
    } else if ((isMapPage || isRecordPage || isStoryPage) && isInitialLoad) {
      footerEl.style.display = 'none';

      headerEl.setAttribute('inert', true)
      footerEl.setAttribute('inert', true)
    } else if (!isMapPage && !isRecordPage && !isStoryPage) {
      headerEl.style.display = 'block';
      footerEl.style.display = 'block';

      headerEl.removeAttribute('inert')
      footerEl.removeAttribute('inert')
    }

  }, [pathname, isMapPage, isStoryPage, isRecordPage, isInitialLoad])

  // Handle route changes
  useEffect(() => {
    if (!hasAnimated.current) {
      return
    }

    if (isRouteChanging === isRouteChangingRef.current && isMapPage === isMapPageRef.current && isRecordPage === isRecordPageRef.current && isStoryPage === isStoryPageRef.current) {
      return
    }

    isRouteChangingRef.current = isRouteChanging;
    isMapPageRef.current = isMapPage;
    isRecordPageRef.current = isRecordPage;
    isStoryPageRef.current = isStoryPageRef;

    if (!isRouteChanging) {
      delayRef.current = computeDelay();
    }

    if (!isRouteChanging && (isMapPage || isRecordPage || isStoryPage) && !isInitialLoad) {
      // setIsHidden(false);
    } else if (isRouteChanging && (isRecordPage || isStoryPage) && !isInitialLoad) {
      delayRef.current = 0;
      // setIsHidden(true);
    } else {
      if (isInitialLoad) {
        isInstant.current = true;
      }
      setIsHidden(isRouteChanging);
    }
  }, [isRouteChanging, isMapPage, isRecordPage, isStoryPage, isInitialLoad]);

  // Handle reduced motion preferences
  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([nav.current, document.documentElement.querySelector('#HeaderRegion')], {
        y: 0
      });
    });
  });

  const hasAnimated = useRef(false);

  const computeDelay = useCallback(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 0
    }

    if ((isRecordPage || isStoryPage) && isInitialLoad) {
      return 0.3
    }

    return pathnameRef.current === STORIES_BASE_PATH && !aboutAnimationCompleted ? 2.5 : 0.9
  }, [isMobile, pathnameRef.current, isRecordPage, isStoryPage, isInitialLoad, aboutAnimationCompleted])

  useLayoutEffect(() => {
    const delay = computeDelay();
    setTimeout(() => {
      delayRef.current = 0;
      setIsHidden(false)
      hasAnimated.current = true;
    }, delay * 1000);
  }, [])

  return { nav };
}
